import { action, computed, observable, makeAutoObservable } from 'mobx';
import { MobxMapAutoNew } from 'src_common/common/mobx-utils/MobxMapAutoNew';
import { TopEventsEventsState, CompetitionCombinedType } from 'src/AppState/CmsState/TopEventsState/TopEventsState';
import { TopEventsEventCompetitionState } from './TopEventsCompetitionState';
import { TrpcClient } from 'src/AppState/TrpcClient';
import { Response200Type } from 'src/api_openapi/generated/openapi_website_cms_getAllTopEventsSports';

export type TopEventsSportDataModelType = Response200Type[number];

export class TopEventsSportState {
    private readonly topEventsEventCompetitionStateMap: MobxMapAutoNew<number, TopEventsEventCompetitionState>;

    @observable public isOpenSection: boolean = false;
    @observable public isActive: boolean = false;
    @observable public showDeletePopup: boolean = false;

    public constructor(
        private readonly trpcClient: TrpcClient,
        private readonly sportSlug: string,
        private readonly getSportData: (sportSlug: string) => TopEventsSportDataModelType | null,
        private readonly topEventsEventsSectionState: TopEventsEventsState
    ) {
        this.isActive = this.getSportData(this.sportSlug)?.is_active ?? false;
        this.topEventsEventCompetitionStateMap = new MobxMapAutoNew(
            (competitionId: number): TopEventsEventCompetitionState =>
                new TopEventsEventCompetitionState(
                    trpcClient,
                    competitionId,
                    this.getCompetitionData,
                    this.handleDeleteSport,
                    topEventsEventsSectionState
                )
        );
        makeAutoObservable(this);
    }

    @computed private get sportData(): TopEventsSportDataModelType | null {
        return this.getSportData(this.sportSlug) ?? null;
    }

    @action public getCompetitionData = (competitionId: number): CompetitionCombinedType | null => {
        return (
            this.sportData?.competitions_list.find((competition) => competition.competition_id === competitionId) ??
            null
        );
    };

    public getTopEventsEventCompStateByCompId = (competitionId: number): TopEventsEventCompetitionState => {
        return this.topEventsEventCompetitionStateMap.get(competitionId);
    };

    @action public setSportActive = async (): Promise<void> => {
        if (this.sportData !== null) {
            const item = this.getSportData(this.sportSlug);

            if (item === null) {
                console.error('Cannot find the sport with provided slug.');
                return;
            }

            const topEventsSportDetailsData = {
                ...item,
                is_active: !item.is_active,
            };
            this.isActive = !this.isActive;

            try {
                await this.trpcClient.client.topEventsSports.updateTopEventsSport.mutate({
                    data: topEventsSportDetailsData,
                    id: this.sportData.id,
                });

                await this.topEventsEventsSectionState.topEventsEventsResource.refresh();
                this.topEventsEventsSectionState.setSportsDataFromResource();
            } catch (error) {
                console.error(error);
            }
        }
    };

    @action public handleToggleOpenSection = (): void => {
        this.isOpenSection = !this.isOpenSection;
    };

    @action public handleHideDeletePopup = (): void => {
        this.showDeletePopup = false;
    };

    @action public handleShowDeletePopup = (): void => {
        this.showDeletePopup = true;
    };

    @action public handleDeleteSport = async (): Promise<void> => {
        if (this.sportData !== null) {
            const removeResult = await this.trpcClient.client.topEventsSports.deleteTopEventsSport.mutate({
                id: this.sportData.id,
            });

            if (removeResult.status === 200) {
                await this.handleDeleteAllCompetitions();
                await this.topEventsEventsSectionState.topEventsEventsResource.refresh();
                this.topEventsEventsSectionState.setSportsDataFromResource();
            } else {
                console.error("The item couldn't be deleted.");
            }
        }
    };

    @action public handleDeleteAllCompetitions = async (): Promise<void> => {
        const competitions = this.sportData?.competitions_list ?? [];
        const competitionIds = competitions.map((competition) => competition.id);

        for (const competition of competitions) {
            const eventIds = competition.events_list.map((e) => e.id);
            await this.handleDeleteAllEvents(eventIds);
        }

        for (const id of competitionIds) {
            const removeResult = await this.trpcClient.client.topEventsCompetitions.deleteTopEventsCompetition.mutate({
                id,
            });

            if (removeResult.status === 200) {
                await this.topEventsEventsSectionState.topEventsEventsResource.refresh();
            } else {
                console.error("The item couldn't be deleted.");
            }
        }
    };

    @action public handleDeleteAllEvents = async (eventsIds: Array<number>): Promise<void> => {
        for (const id of eventsIds) {
            const removeResult = await this.trpcClient.client.topEventsEvents.deleteTopEventsEvent.mutate({ id });

            if (removeResult.status === 200) {
                await this.topEventsEventsSectionState.topEventsEventsResource.refresh();
            } else {
                console.error("The item couldn't be deleted.");
            }
        }
    };
}
